/**
 * WAF home / overview analytics (Owl homeAnalytics + historicalReport) — the
 * dashboard data layer. Service-scoped (path: `/services/:service_id/`) over a date range.
 *
 * Scope is mutually exclusive: a single site (`site_domain` for home/*,
 * `site_id` for historical/*) XOR the whole-org overview (both empty → the
 * backend resolves the org from the token).
 *
 * Backed by `modules/service/waf/controller_home.go` + `controller_historical.go`:
 *   - GET /services/:service_id/waf/home/report             → range roll-up (traffic/requests/cache-hit)
 *   - GET /services/:service_id/waf/home/diagram            → metric time series
 *   - GET /services/:service_id/waf/home/by-group           → usage grouped by platform | country
 *   - GET /services/:service_id/waf/historical/top-optimizations → top-N rankings per category
 *   - GET /services/:service_id/waf/historical/usage        → per-usage-type series + period compare
 */
import { useQuery } from '@tanstack/react-query';
import { serviceApiV3, wafKeys, unwrap } from './_shared';

/** Resolved dashboard scope + range passed to every home/historical hook. */
export interface WafHomeScope {
  /** Site FQDN for `/waf/home/*` (empty = org overview). */
  siteDomain?: string;
  /** Zone id for `/waf/historical/*` (empty = org overview). */
  siteId?: string;
  startTime: number;
  endTime: number;
}

/** Overview range roll-up behind the headline cards. */
export interface WafHomeSummary {
  total_traffic_bytes: number;
  total_requests: number;
  cache_hit_rate: number;
}

/** One bucket of the by-group breakdown (platform XOR country per `group_by`). */
export interface WafHomeGroupUsage {
  platform?: string;
  country?: string;
  total_traffic_bytes?: number;
  total_requests?: number;
}

/** One row of the overview top-domains ranking (only the requested metric set). */
export interface WafHomeTopDomain {
  domain_name: string;
  total_traffic_bytes?: number;
  total_requests?: number;
}

/** One line of an overview diagram; `series` maps each metric to its values. */
export interface WafHomeDiagramSeries {
  label: string;
  type?: string;
  series: Record<string, (number | null)[]>;
}

/** Overview metric time series (shared x-axis `labels`, unix millis). */
export interface WafHomeDiagram {
  labels: number[];
  datasets: WafHomeDiagramSeries[];
}

/** One ranked entry of a top-optimizations category (a key + its count). */
export interface WafTopOptimizationBucket {
  key: string;
  count: number;
}

/** One requested category's ranking. */
export interface WafTopOptimizationCategory {
  category: string;
  data: WafTopOptimizationBucket[];
}

/** Historical top-optimizations report (one ranking per category) + total. */
export interface WafTopOptimizations {
  data: WafTopOptimizationCategory[];
  total_count: number;
}

/** A usage series' period-over-period comparison. */
export interface WafUsageCompare {
  comparable: boolean;
  reason: string | null;
  compare_percentage: number;
  compare_value: number;
}

/** One usage-type historical series + its period compare. */
export interface WafUsageDiagram {
  labels: number[];
  datasets: number[];
  compare_result: WafUsageCompare;
}

const enabledFor = (serviceId: string | undefined, scope: WafHomeScope) =>
  !!serviceId && !!scope.startTime && !!scope.endTime;

/** Shared params for the `/waf/home/*` family (site_domain XOR org overview). */
const homeParams = (scope: WafHomeScope, extra: Record<string, unknown> = {}) => ({
  site_domain: scope.siteDomain || undefined,
  start_time: scope.startTime,
  end_time: scope.endTime,
  ...extra,
});

/** Shared params for the `/waf/historical/*` family (site_id XOR org overview). */
const histParams = (scope: WafHomeScope, extra: Record<string, unknown> = {}) => ({
  site_id: scope.siteId || undefined,
  start_time: scope.startTime,
  end_time: scope.endTime,
  ...extra,
});

/** Overview range summary (traffic / requests / cache-hit-rate). */
export function useWafHomeReport(serviceId: string | undefined, scope: WafHomeScope) {
  const params = homeParams(scope);
  return useQuery<WafHomeSummary>({
    queryKey: wafKeys.home(serviceId ?? '', 'report', params),
    enabled: enabledFor(serviceId, scope),
    staleTime: 60_000,
    queryFn: async () => unwrap<WafHomeSummary>(await serviceApiV3.get(`/services/${serviceId}/waf/home/report`, { params })),
  });
}

/** Overview usage grouped by `platform` | `country`. */
export function useWafHomeByGroup(
  serviceId: string | undefined,
  scope: WafHomeScope,
  groupBy: 'platform' | 'country',
  metric = 'traffic_bytes',
) {
  const params = homeParams(scope, { group_by: groupBy, metric });
  return useQuery<WafHomeGroupUsage[]>({
    queryKey: wafKeys.home(serviceId ?? '', 'by-group', params),
    enabled: enabledFor(serviceId, scope),
    staleTime: 60_000,
    queryFn: async () =>
      unwrap<WafHomeGroupUsage[]>(await serviceApiV3.get(`/services/${serviceId}/waf/home/by-group`, { params })) ?? [],
  });
}

/** Overview metric time series. */
export function useWafHomeDiagram(serviceId: string | undefined, scope: WafHomeScope, metric = 'traffic_bytes') {
  const params = homeParams(scope, { metric });
  return useQuery<WafHomeDiagram>({
    queryKey: wafKeys.home(serviceId ?? '', 'diagram', params),
    enabled: enabledFor(serviceId, scope),
    staleTime: 60_000,
    queryFn: async () => unwrap<WafHomeDiagram>(await serviceApiV3.get(`/services/${serviceId}/waf/home/diagram`, { params })),
  });
}

/** Overview top-domains ranking under a site (needs a `site_domain` scope). */
export function useWafHomeTopDomains(serviceId: string | undefined, scope: WafHomeScope, metric = 'traffic_bytes') {
  const params = homeParams(scope, { metric });
  return useQuery<WafHomeTopDomain[]>({
    queryKey: wafKeys.home(serviceId ?? '', 'top-domains', params),
    enabled: enabledFor(serviceId, scope) && !!scope.siteDomain,
    staleTime: 60_000,
    queryFn: async () =>
      unwrap<WafHomeTopDomain[]>(await serviceApiV3.get(`/services/${serviceId}/waf/home/top-domains`, { params })) ?? [],
  });
}

/** Historical top-N rankings for the given categories (e.g. country, cdn, site, domain). */
export function useWafTopOptimizations(
  serviceId: string | undefined,
  scope: WafHomeScope,
  categories: string[],
) {
  const params = histParams(scope, { categories: categories.join(',') });
  return useQuery<WafTopOptimizations>({
    queryKey: wafKeys.home(serviceId ?? '', 'top-optimizations', params),
    enabled: enabledFor(serviceId, scope) && categories.length > 0,
    staleTime: 60_000,
    queryFn: async () =>
      unwrap<WafTopOptimizations>(await serviceApiV3.get(`/services/${serviceId}/waf/historical/top-optimizations`, { params })),
  });
}

/** Per-usage-type historical series (with period-over-period compare). */
export function useWafHistoricalUsage(
  serviceId: string | undefined,
  scope: WafHomeScope,
  usageTypes: string[],
) {
  const params = histParams(scope, { usage_types: usageTypes.join(',') });
  return useQuery<Record<string, WafUsageDiagram>>({
    queryKey: wafKeys.home(serviceId ?? '', 'usage', params),
    enabled: enabledFor(serviceId, scope) && usageTypes.length > 0,
    staleTime: 60_000,
    queryFn: async () =>
      unwrap<Record<string, WafUsageDiagram>>(await serviceApiV3.get(`/services/${serviceId}/waf/historical/usage`, { params })) ?? {},
  });
}
